import client from '../../api/client';

export type CalendarSpace = {
  id: string;
  title: string;
  createdAt: string;
  ownerKey?: string;
};

const CALENDAR_SPACES_KEY = 'boardly.calendar.spaces';
const DEFAULT_OWNER_KEY = 'anonymous';

const canUseCalendarApi = () => {
  if (typeof window === 'undefined') return false;
  return Boolean(window.localStorage.getItem('authToken'));
};

const readCalendarSpaces = (): CalendarSpace[] => {
  if (typeof window === 'undefined') return [];

  try {
    const rawValue = window.localStorage.getItem(CALENDAR_SPACES_KEY);
    if (!rawValue) return [];
    const parsed = JSON.parse(rawValue);
    if (!Array.isArray(parsed)) return [];

    return parsed.filter((item): item is CalendarSpace => (
      typeof item?.id === 'string' &&
      typeof item?.title === 'string' &&
      typeof item?.createdAt === 'string'
    ));
  } catch {
    return [];
  }
};

const writeCalendarSpaces = (spaces: CalendarSpace[]) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(CALENDAR_SPACES_KEY, JSON.stringify(spaces));
};

const upsertCalendarSpace = (space: CalendarSpace) => {
  const spaces = readCalendarSpaces();
  writeCalendarSpaces([space, ...spaces.filter(item => item.id !== space.id)]);
};

const syncCalendarSpaceToApi = async (space: CalendarSpace): Promise<CalendarSpace> => {
  const response = await client.post<CalendarSpace>('/calendar-spaces/', {
    id: space.id,
    title: space.title,
  });
  upsertCalendarSpace(response.data);
  return response.data;
};

export const getCalendarSpaces = (): CalendarSpace[] => readCalendarSpaces();

export const getCalendarSpace = (id?: string): CalendarSpace | null => {
  if (!id) return null;
  return readCalendarSpaces().find(space => space.id === id) ?? null;
};

export const getUserCalendarSpace = (ownerKey: string = DEFAULT_OWNER_KEY): CalendarSpace | null => {
  const spaces = readCalendarSpaces();
  return spaces.find(space => (space.ownerKey ?? DEFAULT_OWNER_KEY) === ownerKey) ?? null;
};

export const createCalendarSpace = (title: string, ownerKey: string = DEFAULT_OWNER_KEY): CalendarSpace => {
  const existing = getUserCalendarSpace(ownerKey);
  if (existing) return existing;

  const now = new Date();
  const space: CalendarSpace = {
    id: ownerKey === DEFAULT_OWNER_KEY ? `${now.getTime()}` : `calendar-${ownerKey}`,
    title,
    createdAt: now.toISOString(),
    ownerKey,
  };
  const spaces = readCalendarSpaces().filter(item => (item.ownerKey ?? DEFAULT_OWNER_KEY) !== ownerKey);
  writeCalendarSpaces([space, ...spaces]);
  return space;
};

export const ensureCalendarSpace = (title: string, ownerKey: string = DEFAULT_OWNER_KEY): CalendarSpace => {
  return getUserCalendarSpace(ownerKey) ?? createCalendarSpace(title, ownerKey);
};

export const getCalendarSpaceOnline = async (id?: string): Promise<CalendarSpace | null> => {
  if (!id) return null;
  if (!canUseCalendarApi()) return getCalendarSpace(id);

  try {
    const response = await client.get<CalendarSpace>(`/calendar-spaces/${encodeURIComponent(id)}/`);
    upsertCalendarSpace(response.data);
    return response.data;
  } catch {
    const localSpace = getCalendarSpace(id);
    if (!localSpace) return null;
    try {
      return await syncCalendarSpaceToApi(localSpace);
    } catch {
      return localSpace;
    }
  }
};

export const getUserCalendarSpaceOnline = async (ownerKey: string = DEFAULT_OWNER_KEY): Promise<CalendarSpace | null> => {
  if (!canUseCalendarApi()) return getUserCalendarSpace(ownerKey);

  try {
    const response = await client.get<CalendarSpace[]>('/calendar-spaces/');
    response.data.forEach(upsertCalendarSpace);
    if (response.data[0]) return response.data[0];
    const localSpace = getUserCalendarSpace(ownerKey);
    if (!localSpace) return null;
    return await syncCalendarSpaceToApi(localSpace);
  } catch {
    return getUserCalendarSpace(ownerKey);
  }
};

export const createCalendarSpaceOnline = async (
  title: string,
  ownerKey: string = DEFAULT_OWNER_KEY
): Promise<CalendarSpace> => {
  const localSpace = createCalendarSpace(title, ownerKey);
  if (!canUseCalendarApi()) return localSpace;

  try {
    return await syncCalendarSpaceToApi(localSpace);
  } catch {
    return localSpace;
  }
};

export const ensureCalendarSpaceOnline = async (
  title: string,
  ownerKey: string = DEFAULT_OWNER_KEY
): Promise<CalendarSpace> => {
  const existing = await getUserCalendarSpaceOnline(ownerKey);
  return existing ?? createCalendarSpaceOnline(title, ownerKey);
};
